'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import store from 'store';
import { getStepsForMeals } from '@utils/Tasks';
import ChatActions from '@actions/ChatActions';
import ChatStore from '@stores/ChatStore';
import { toHuman } from '@utils/Duration';
import { roundForHumans, smartCeil, convertDecimalToFraction } from '@utils/Math';

export default class MealPrepSteps extends Component {
    static contextTypes = {
        user: PropTypes.object,
        meals: PropTypes.array,
        recipes: PropTypes.object,
        details: PropTypes.object,
        foods: PropTypes.object,
    };

    componentDidMount() {
        this.processPrepSteps();
        
        // Listen for chat store changes to handle read markers
        ChatStore.addChangeListener(this.onChatStoreChange);
    }
    
    componentWillUnmount() {
        // Clean up listener when component unmounts
        ChatStore.removeChangeListener(this.onChatStoreChange);
    }
    
    onChatStoreChange = () => {
        const botJid = 'eatlove-prep-bot@chat.eatlove.is';
        const chat = ChatStore.getChatWithJid(botJid);

        if (chat.totals.unread == 0 && chat.markers.read) {
            // Store the last read prep marker in localStorage
            store.set('prep-ahead-read-marker', chat.markers.read.format(), new Date().getTime() + 1000 * 3600 * 8);
        }
    }

    // Store previous context values in state to detect changes
    state = {
        prevUser: null,
        prevMeals: null,
        prevRecipes: null,
        prevDetails: null,
        prevFoods: null,
    };

    static getDerivedStateFromProps(props, state) {
        // This method doesn't have access to this.context, we'll check in componentDidUpdate
        return null;
    }

    componentDidUpdate() {
        // Check for context changes by comparing with stored values
        const { user, meals, recipes, details, foods } = this.context;
        const { prevUser, prevMeals, prevRecipes, prevDetails, prevFoods } = this.state;

        // If component mounted or context changed, update state and process steps
        if (user !== prevUser || meals !== prevMeals || recipes !== prevRecipes ||
            details !== prevDetails || foods !== prevFoods) {

            // Update stored context values
            this.setState({
                prevUser: user,
                prevMeals: meals,
                prevRecipes: recipes,
                prevDetails: details,
                prevFoods: foods
            });

            this.processPrepSteps();
        }
    }

    processPrepSteps = () => {
        const { user, meals, recipes, details, foods } = this.context;

        if (!user || !meals || !recipes || !details || !foods) {
            return;
        }

        // Find just a list of the next 5 days meals
        const startDate = moment(), endDate = moment().add(5, 'day');
        const upcomingMeals = meals.filter(
            (meal) => startDate.isSameOrBefore(meal.date, 'day') && endDate.isSameOrAfter(meal.date, 'day')
        );

        // Filter steps for today
        const steps = getStepsForMeals(upcomingMeals, recipes, details, foods).filter((step) =>
            step.date.isSame(moment(), 'day')
        );

        // Generate the prep steps message
        const prepSteps = steps.filter((step) => step.type !== 'sunbasket-deadline');
        if (prepSteps.length === 0) {
            setTimeout(() => ChatActions.updateFakeMessage(
                'prep-ahead',
                'You have no prep-ahead tasks today. Check back with me daily to chop ahead and save time. Enjoy your day!',
                true
            ));
        } else {
            const message = this.generatePrepStepsMessage(prepSteps);

            setTimeout(() => ChatActions.updateFakeMessage('prep-ahead', message));
        }
    }

    renderFraction = ({ whole, numerator, denominator }, skipZero = false) => {
        if (!denominator && !whole && skipZero) {
            return '';
        }

        if (!denominator && !whole && !skipZero) {
            return '0';
        }

        if (denominator && !whole) {
            return `${numerator}/${denominator}`;
        }

        if (whole && !denominator) {
            return whole;
        }

        return `${whole} ${numerator}/${denominator}`;
    };

    getAmountFromStep = (step) => {
        const { user } = this.context;

        // We need to convert the grams of stuff we need to the foods default unit, if it has one.
        var amount = Math.round(step.grams_needed) + 'g';

        if (step.food && step.food.default_unit && step.food.default_unit.grams) {
            // Calculate the actual amount needed accounting for the unit's amount field
            const unitsNeeded = step.grams_needed / step.food.default_unit.grams;
            const actualAmount = step.food.default_unit.amount || 1;
            var t = smartCeil(unitsNeeded * actualAmount);

            let content = [
                user.units_mode === 'metric' ? roundForHumans(t) : this.renderFraction(convertDecimalToFraction(t)),
            ];

            if (step.food.default_unit.description) {
                content.push(step.food.default_unit.description);
            }

            amount = content.filter((v) => v).join(' ');
        }

        return amount;
    };

    getDaysFromStep = (step) => {
        // Figure out what days this step is for, we should have a list of meals
        var days = step.meals.map((meal) => moment(meal.date).format('dddd'));

        return days.filter((value, key) => days.indexOf(value) == key);
    }

    renderFoundationalRecipe = (step) => {
        const details = step.subdetails;

        if (!details) {
            return '';
        }

        // How many of the foundational recipes do we need to make?
        var quantity = Math.ceil(step.grams_needed / (step.subrecipe.grams_per_serving * step.subrecipe.servings));
        let batches = '';

        if (quantity > 1) {
            batches = `, make ${quantity} batches`;
        }

        let timing = '';

        if (step.subrecipe.total_time) {
            timing = ' (' + toHuman(step.subrecipe.total_time, true) + ')';
        }

        return (
            'Prepare ' + step.name + ' ' + batches + timing
        );
    };

    renderInstructions = (step) => {
        if (!step.details) {
            return '';
        }

        let batches = '';

        // How many of the foundational recipes do we need to make?
        if (step.scaling > 1) {
            batches = `, make ${step.scaling} batches`;
        }

        let timing = '';

        if (step.total_time) {
            timing = ' (' + toHuman(step.total_time, true) + ')';
        }

        return (
            'Prepare ' + step.name + ' ' + batches + timing
        );
    };

    renderThawStep = (step) => {
        const { recipes } = this.context;
        const recipe = recipes?.[step.meal.recipe_uuid];

        if (!recipe) {
            return '';
        }

        return 'Thaw leftover: ' + recipe.title;
    };

    renderStep = (step) => {
        if (step.type === 'foundational') {
            return this.renderFoundationalRecipe(step);
        }

        if (step.type === 'instructions') {
            return this.renderInstructions(step);
        }

        if (step.type === 'thaw') {
            return this.renderThawStep(step);
        }

        let contents = [];

        let stepVerbs = step.prep_steps.map((prep) => prep.present_simple || prep.prep_step).join(', ');
        let prepTime = step.prep_steps.reduce((cv, prep) => {
            return (prep.prep_time || 300) + cv;
        }, 0);

        if (stepVerbs) {
            contents.push(stepVerbs);
        }

        contents.push(this.getAmountFromStep(step, 2));

        if (step.name) {
            contents.push(step.name);
        }

        let timing = '';

        if (prepTime) {
            timing = ' (' + toHuman(prepTime, true) + ')';
        }

        return contents.join(' ') + timing;
    };

    generatePrepStepsMessage = (steps) => {
        const { user } = this.context;
        if (!user || !steps.length) return null;

        const { name, email } = user;
        let message = `Hello ${name || email}, it's time to start prepping!\n`;

        let lastStepDay = null;
        steps.forEach((step) => {
            const stepDays = step.meals.map((meal) => moment(meal.date).format('dddd'))
                .filter((value, key, self) => self.indexOf(value) === key);
            const stepDay = stepDays[0];

            if (stepDay !== lastStepDay) {
                message += `\n## For ${stepDay}'s meal:\n`;
                lastStepDay = stepDay;
            }

            // Specific rendering for each step type
            message += `* ${this.renderStep(step)}\n`;
        });

        return message;
    };

    render() {
        // This component doesn't render any UI
        return null;
    }
}
